import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { AppButton, AppHeaderLogo } from '../../components';
import { selectEventsData, setEventDataList } from '../../store/events';
import { sendAnalyticsEvents } from '../../analytics/common';
import { EVENTS } from '../../analytics/events';

const isArabic = navigator.language.startsWith('ar');
const textAlign = isArabic ? 'right' : 'left';

const Icon = ({ $color }) => (
  <svg fill="none" height="20" viewBox="0 0 20 20" width="20">
    <path
      clipRule="evenodd"
      d="m14.2955 7.60549-4.56801 6.00001c-.188.247-.479.393-.789.395h-.007c-.307 0-.597-.142-.787-.384l-2.432-3.107c-.34-.434-.264-1.06301.171-1.40301.434-.341 1.064-.265 1.404.171l1.633 2.08601 3.78401-4.96901c.334-.439.961-.525 1.402-.19.439.335.524.962.189 1.401zm-4.295-7.60500172c-5.52301 0-10.00001172 4.47700172-10.00001172 10.00001172 0 5.522 4.47700172 10 10.00001172 10 5.523 0 10-4.478 10-10 0-5.52301-4.477-10.00001172-10-10.00001172z"
      fill={$color}
      fillRule="evenodd"
    />
  </svg>
);

const QuestionTitle = styled.h1`
  font-weight: 700;
  font-size: 24px;
  line-height: 31.2px;
  color: #1e1e1e;
  margin: 0 0 16px 0;
  white-space: break-spaces;
  position: relative;
  ${({ $customStyles }) => $customStyles};
`;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  max-width: 327px;
  overflow-y: scroll;
  position: relative;
  margin-bottom: ${({ $isMultiple }) => $isMultiple && '75px'};
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: none;
  scroll-behavior: smooth;
`;

const ContentWrapper = styled.div``;

const StyledList = styled.ul`
  list-style: none;
  width: 100%;
  margin-top: 16px;
`;

const StyledItem = styled.li`
  width: 100%;
  max-height: 56px;
  display: flex;
  flex-direction: row ${isArabic ? '-reverse' : ''};
  align-items: center;
  background-color: ${({ $active }) => ($active ? '#ebf5ff' : '#F2F2F2')};
  padding: 19px 0px 19px 24px;
  border-radius: 12px;
  position: relative;
  cursor: pointer;
  color: #323232;
  > svg {
    position: absolute;
    right: 16px;
  }
  ${({ $multipleActive }) =>
    $multipleActive &&
    css`
      background-color: #ebf5ff;
    `}
  ${({ $centered }) =>
    $centered &&
    css`
      padding: 19px;
    `}
  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const StyledItemTitle = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 18.4px;
  text-align: ${textAlign};
  white-space: pre-line;
  width: 100%;
  flex-grow: 1;
  ${({ $centered }) =>
    $centered &&
    css`
      text-align: center;
    `}
`;

const QuestionContainer = ({
  history,
  title,
  options,
  nextUrl,
  screenName,
  multiple = false,
  centered = false,
}) => {
  const dispatch = useDispatch();
  const [chosenOptions, setChosenOptions] = useState([]);
  const eventsData = useSelector(selectEventsData);
  const navigate = useNavigate();

  const onOptionClick = (answer) => {
    const localEvents = {
      screen_name: screenName,
      question: title,
      answer,
    };
    dispatch(setEventDataList(localEvents));
    sendAnalyticsEvents(EVENTS.onboardingQuestionPageSubmitted, {
      ...eventsData,
      ...localEvents,
    });
    if (multiple) {
      navigate(`/${nextUrl}`);
    } else {
      setTimeout(() => {
        navigate(`/${nextUrl}`);
      }, 500);
    }
  };
  const renderOption = (item, i) => {
    const isActive = chosenOptions === item.value;
    const isActiveMultiple = chosenOptions.includes(item.value);
    const handleClick = () => {
      if (multiple) {
        setChosenOptions((prevOptions) => {
          const newOptions = isActiveMultiple
            ? prevOptions.filter((option) => option !== item.value)
            : [...prevOptions, item.value];
          return newOptions;
        });
      } else {
        setChosenOptions(item.value);
        onOptionClick(item.value, item.name);
      }
    };

    return (
      <StyledItem
        $active={isActive}
        $centered={centered}
        $multipleActive={multiple && isActiveMultiple}
        $multiple={multiple}
        key={item.id}
        onClick={handleClick}
        id={`option-${i + 1}`}
      >
        {item.name && (
          <StyledItemTitle $multiple={multiple} $centered={centered}>
            {item.name}
          </StyledItemTitle>
        )}
        {multiple && <Icon $color={isActiveMultiple ? '#3393ec' : '#abc8e5'} />}
      </StyledItem>
    );
  };

  return (
    <>
     {!centered && <AppHeaderLogo />}
      <Wrapper id="scrollable-div" $isMultiple={multiple}>
        <ContentWrapper>
          <QuestionTitle
            $customStyles={`text-align: ${
              centered ? 'center' : textAlign
            }; word-break: break-word; margin: ${centered && '0px 0px 56px'}`}
          >
            {title}
          </QuestionTitle>
          <StyledList>{options.map(renderOption)}</StyledList>
        </ContentWrapper>
      </Wrapper>
      {multiple && (
        <AppButton
          onClick={() => onOptionClick(chosenOptions)}
          bottomPosition={'24px'}
          disabled={!chosenOptions.length}
          displayStubBtn={false}
          customBeforeHeight="none"
          customBeforeBg="#ffffff"
          customStyles={'min-height:56px'}
        />
      )}
    </>
  );
};

export default QuestionContainer;
